import type { ReactNode } from 'react';
import { Badge } from '@/components/atoms/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { fmt } from '@/components/organisms/sales/sales-document-helpers';
import {
  renderGroupedLines,
  renderGroupedTableRows,
  type BundleGroupableLine,
} from '@/components/organisms/sales/ServiceBundleGroup';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { internalPriceDisplay, type PricingTreatment } from '@/lib/sales/line-economics';
// Same quantity wording the customer's copy of this document uses. "EA" is the
// code for "there is nothing to say about the unit", and an operator gains no
// more from reading it down a column than a customer does.
import { formatQuantityWithUnit } from '@/lib/sales/quantity-display';
import {
  taxBreakdownDisplayMeta,
  taxBreakdownDisplayName,
  type AggregatedTaxBreakdownRow,
} from '@/lib/tax/tax-utils';
import { cn } from '@/lib/utils';

export interface SalesLineItemBadge {
  className?: string;
  label: ReactNode;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
}

/**
 * A row of a read-only sales document.
 *
 * Extends `BundleGroupableLine` so a caller that has the grouping keys can pass
 * them straight through and get the service/parts framing for free. A caller
 * that has no bundles (a pricebook preview, a job-type template) leaves them
 * unset and every row renders exactly as it always did.
 */
export interface SalesLineItemReadOnlyRow extends BundleGroupableLine {
  badges?: SalesLineItemBadge[];
  /**
   * MATERIAL / EQUIPMENT: the catalog's answer to whether the part is billed on
   * its own line, and the default for `pricingTreatment`. Absent is chargeable,
   * so every caller that does not supply either field prints exactly as before.
   */
  chargeable?: boolean | null;
  /** How THIS document charges for the part. Absent derives from `chargeable`. */
  pricingTreatment?: PricingTreatment | null;
  code?: string | null;
  detail?: ReactNode;
  id?: string | null;
  name: string;
  quantity: number;
  /** Per-line tax. Supplying it opts the table into an itemized Tax column. */
  tax?: number;
  /** Every named jurisdiction that contributes to the per-line tax. */
  taxBreakdown?: AggregatedTaxBreakdownRow[];
  /** Unit of measure from the pricebook (e.g. each, SQFT, HR); shown next to qty. */
  unit?: string | null;
  total: number;
  type: string;
  unitPrice: number;
}

function LineTaxBreakdown({ rows }: { rows?: AggregatedTaxBreakdownRow[] }) {
  if (!rows?.length) return null;

  return (
    <div className="mt-1.5 space-y-1 text-[11px] leading-tight text-muted-foreground">
      {rows.map((row, index) => (
        <div
          key={`${row.primary}-${row.secondary ?? ''}-${row.jurisdictionType ?? ''}-${row.rate}-${index}`}
        >
          <p>{taxBreakdownDisplayName(row)}</p>
          <p className="text-[10px] text-muted-foreground/70">
            {taxBreakdownDisplayMeta(row)} · {fmt(row.amount)}
          </p>
        </div>
      ))}
    </div>
  );
}

interface SalesLineItemsReadOnlyTableProps {
  emptyMessage?: string;
  items: SalesLineItemReadOnlyRow[];
  priceLabel?: string;
  tableClassName?: string;
}

export function SalesLineItemsReadOnlyTable({
  emptyMessage = 'No line items',
  items,
  priceLabel = 'Unit Price',
  tableClassName,
}: SalesLineItemsReadOnlyTableProps) {
  const pricingHidden = useIsFieldHidden('PRICING');
  const showTax = items.some((item) => item.tax !== undefined);
  const columnCount = 3 + (pricingHidden ? 0 : (showTax ? 1 : 0) + 2);
  // A bundle's worth is a PRE-TAX figure — the Total column carries tax, and
  // adding a service's tax to its parts' would print a bundle price that is
  // neither the taxable base nor the amount charged. `lineSubtotal` when the
  // caller has it; otherwise back it out of the total.
  const amountOf = (item: SalesLineItemReadOnlyRow) =>
    item.lineSubtotal ?? item.total - (item.tax ?? 0);
  // This table is INTERNAL — the estimate detail, a proposal option, a job-type
  // preview — so a line that charges nothing prints a dash where its price
  // would be rather than asserting a price of $0.00. Bundle and document TOTALS
  // are sums and stay currency; `amountOf` above is deliberately untouched.
  const priceText = (item: SalesLineItemReadOnlyRow, amount: number) =>
    internalPriceDisplay(item, amount, fmt);

  if (items.length === 0) {
    return (
      <div className="rounded-xl border p-8 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      {/* Narrow screens are already a card list, which is what the shared
          bundle card was built for — so it drops straight in here. */}
      <div className="md:hidden space-y-3">
        {renderGroupedLines<SalesLineItemReadOnlyRow>({
          lineItems: items,
          formatCurrency: fmt,
          amountOf,
          hideAmounts: pricingHidden,
          renderRow: (item, index) => (
            <div key={`${item.id ?? item.name}-${index}`} className="rounded-lg border p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1 wrap-anywhere">
                  <p className="font-medium text-sm">{item.name}</p>
                  {item.code && (
                    <p className="font-mono text-xs text-muted-foreground">{item.code}</p>
                  )}
                  {item.detail}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {item.badges?.map((badge, badgeIndex) => (
                    <Badge
                      key={badgeIndex}
                      variant={badge.variant ?? 'outline'}
                      className={cn('text-[10px]', badge.className)}
                    >
                      {badge.label}
                    </Badge>
                  ))}
                  <Badge variant="secondary" className="text-[10px]">
                    {item.type}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2 text-sm">
                <span className="text-muted-foreground">
                  {/* Inline, in a sentence — "3 each x $40.00". */}
                  {formatQuantityWithUnit(item.quantity, item.unit, 'word')}
                  {!pricingHidden ? ` x ${priceText(item, item.unitPrice)}` : ''}
                </span>
                {!pricingHidden ? (
                  <div className="text-right">
                    <span className="font-medium">{priceText(item, item.total)}</span>
                    {showTax ? (
                      <div className="text-xs text-muted-foreground">
                        <p>Tax {item.tax === undefined ? '—' : fmt(item.tax)}</p>
                        <LineTaxBreakdown rows={item.taxBreakdown} />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          ),
        })}
      </div>

      <div className={cn('hidden md:block rounded-xl border overflow-hidden', tableClassName)}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              {!pricingHidden ? (
                <>
                  <TableHead className="text-right">{priceLabel}</TableHead>
                  {showTax ? <TableHead className="text-right">Tax</TableHead> : null}
                  <TableHead className="text-right">Total</TableHead>
                </>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {renderGroupedTableRows<SalesLineItemReadOnlyRow>({
              lineItems: items,
              columnCount,
              formatCurrency: fmt,
              amountOf,
              hideAmounts: pricingHidden,
              renderRow: (item, index, role) => (
                <TableRow
                  key={`${item.id ?? item.name}-${index}`}
                  className={cn(role !== 'standalone' && 'bg-primary/[0.02]')}
                >
                  {/* `TableCell` is nowrap by default — right for a date or an
                      amount, wrong for the one column that carries prose. A long
                      description here used to widen the table into a horizontal
                      scroll instead of wrapping; `wrap-anywhere` also lets an
                      unbroken code or URL break rather than set the column's
                      minimum width.

                      That last part cuts both ways: with no minimum of its own
                      this column shrinks to a single character the moment the
                      table is squeezed, since every other column is nowrap and
                      keeps its width. The floor keeps a name readable in a
                      narrow dialog; past it the table scrolls. */}
                  <TableCell className="min-w-48 align-top whitespace-normal wrap-anywhere">
                    {/* The rule is what says these rows belong to the service
                      above rather than standing beside it. */}
                    <div
                      className={cn(
                        'flex items-start gap-2',
                        role === 'part' && 'ml-2 border-l-2 border-primary/25 pl-3',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{item.name}</p>
                        {item.code && (
                          <p className="font-mono text-xs text-muted-foreground">{item.code}</p>
                        )}
                        {item.detail}
                      </div>
                      {item.badges?.map((badge, badgeIndex) => (
                        <Badge
                          key={badgeIndex}
                          variant={badge.variant ?? 'outline'}
                          className={cn('text-[10px]', badge.className)}
                        >
                          {badge.label}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <Badge variant="secondary" className="text-[10px]">
                      {item.type}
                    </Badge>
                  </TableCell>
                  {/* Same wording as the card list above it. One rule, applied
                      the same way under a Qty heading as anywhere else — a
                      per-surface judgement is what made this inconsistent. */}
                  <TableCell className="text-right align-top">
                    {formatQuantityWithUnit(item.quantity, item.unit, 'word')}
                  </TableCell>
                  {!pricingHidden ? (
                    <>
                      <TableCell className="text-right align-top">
                        {priceText(item, item.unitPrice)}
                      </TableCell>
                      {showTax ? (
                        <TableCell className="text-right align-top">
                          <div>{item.tax === undefined ? '—' : fmt(item.tax)}</div>
                          <LineTaxBreakdown rows={item.taxBreakdown} />
                        </TableCell>
                      ) : null}
                      <TableCell className="text-right align-top font-medium">
                        {priceText(item, item.total)}
                      </TableCell>
                    </>
                  ) : null}
                </TableRow>
              ),
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
